import dayjs from 'dayjs';
import type { BudgetForecastDataItem } from 'lumen/models';
import type { SegmentLevel } from 'lumen/utils/constants/budget-forecast-constants';
import safeDivide from 'lumen/utils/numeric/safe-divide';

const SEGMENT_LEVEL_TO_DEPTH: Record<SegmentLevel, number> = {
  totals: 0,
  groups: 1,
  categories: 2,
  sub_categories: 3,
} as const;

function _isItemInStartAndEndDate(
  item: BudgetForecastDataItem,
  options: {
    endDate: string;
    startDate: string;
  },
): boolean {
  return (
    item.date.isSameOrAfter(dayjs.utc(options.startDate)) &&
    item.date.isSameOrBefore(dayjs.utc(options.endDate))
  );
}

function _isItemInSelectedWeekdays(
  item: BudgetForecastDataItem,
  options: {
    selectedWeekdays: string[];
  },
): boolean {
  if (item.dateAggregation === 'month') {
    return true;
  }

  return options.selectedWeekdays.includes(`${item.date.isoWeekday()}`);
}

function _isItemInSelectedSegments(
  item: BudgetForecastDataItem,
  options: {
    segmentLevel: SegmentLevel;
    selectedSegmentIds: string[];
  },
): boolean {
  if (SEGMENT_LEVEL_TO_DEPTH[options.segmentLevel] === 0) {
    return item.isRoot;
  }

  return Boolean(
    item.depth === SEGMENT_LEVEL_TO_DEPTH[options.segmentLevel] &&
    item.segment?.id &&
    options.selectedSegmentIds.includes(item.segment.id),
  );
}

function _getNewValue(
  item: BudgetForecastDataItem,
  options: {
    changeType: 'absolute' | 'exact' | 'percentage';
    changeValue: number;
    metric: 'adr' | 'rooms';
  },
): number {
  const metricToShowProperty =
    options.metric === 'adr' ? 'adrToShow' : 'roomsToShow';

  if (options.changeType === 'absolute') {
    return item[metricToShowProperty] + options.changeValue;
  }

  if (options.changeType === 'exact') {
    return options.changeValue;
  }

  if (options.changeType === 'percentage') {
    const percentageChange = safeDivide(options.changeValue, 100) ?? 0;
    return item[metricToShowProperty] * (1 + percentageChange);
  }

  return options.changeType satisfies never;
}

function _applyChangeToItem(
  item: BudgetForecastDataItem,
  options: {
    changeType: 'absolute' | 'exact' | 'percentage';
    changeValue: number;
    metric: 'adr' | 'rooms';
  },
): void {
  const newValue = _getNewValue(item, options);
  const clampedValue = Math.max(newValue, 0);

  item[options.metric] = clampedValue;
}

export function applyBulkEditToItems(
  items: BudgetForecastDataItem[],
  options: {
    changeType: 'absolute' | 'exact' | 'percentage';
    changeValue: number | null;
    endDate: string;
    metric: 'adr' | 'rooms';
    segmentLevel: SegmentLevel;
    selectedSegmentIds: string[];
    selectedWeekdays: string[];
    startDate: string;
  },
): void {
  const { changeValue } = options;
  if (changeValue === null) {
    return;
  }

  const itemsToUpdate = items
    .filter((item) => _isItemInStartAndEndDate(item, options))
    .filter((item) => _isItemInSelectedWeekdays(item, options))
    .filter((item) => _isItemInSelectedSegments(item, options));

  itemsToUpdate.forEach((item) =>
    _applyChangeToItem(item, { ...options, changeValue }),
  );
}
